import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import MarkdownEditor from "@/components/MarkdownEditor";
import { FileText, Plus, Trash2, Save, X, ArrowLeft, Edit3, ExternalLink } from "lucide-react";

const BLANK = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "General",
  tags: [],
  author: "SerpNex Team",
  meta_title: "",
  meta_description: "",
  canonical_url: "",
  status: "published",
  cover_image: "",
};

function toSlug(s) {
  return (s || "").toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 90);
}

export default function CmsPage() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | slug
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [tagsText, setTagsText] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);

  const load = () => api.get("/blog/admin/all").then((r) => setPosts(r.data)).catch(() => toast.error("Failed to load posts"));
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm(BLANK);
    setTagsText("");
    setSlugDirty(false);
  };

  const startEdit = async (slug) => {
    try {
      const r = await api.get(`/blog/admin/${slug}`);
      setEditing(slug);
      setForm({ ...BLANK, ...r.data });
      setTagsText((r.data.tags || []).join(", "));
      setSlugDirty(true);
    } catch { toast.error("Post not found"); }
  };

  const cancel = () => { setEditing(null); setForm(BLANK); setTagsText(""); setSlugDirty(false); };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/blog/${slug}`);
      toast.success("Post deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const save = async () => {
    const payload = {
      ...form,
      tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean),
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      canonical_url: form.canonical_url || null,
      cover_image: form.cover_image || null,
    };
    if (!payload.title || !payload.slug || !payload.excerpt || !payload.content) {
      toast.error("Title, slug, excerpt and content are all required.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/blog", payload);
        toast.success("Post published");
      } else {
        await api.put(`/blog/${editing}`, payload);
        toast.success("Post updated");
      }
      load();
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (editing !== null) {
    return (
      <div className="p-8 lg:p-10 max-w-6xl">
        <button onClick={cancel} data-testid="cms-back" className="inline-flex items-center gap-1 text-xs text-[#525B6A] hover:text-[#0047FF]"><ArrowLeft className="h-3 w-3" /> Back to posts</button>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">{editing === "new" ? "New post" : "Edit post"}</div>
            <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">{form.title || "Untitled"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cancel} data-testid="cms-cancel" className="btn-ghost px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-1"><X className="h-4 w-4" /> Cancel</button>
            <button onClick={save} disabled={saving} data-testid="cms-save" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</button>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Title</label>
              <input data-testid="cms-title" value={form.title} onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({ ...prev, title: v, slug: slugDirty ? prev.slug : toSlug(v) }));
              }} placeholder="How to interpret a keyword drop" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-lg font-display font-semibold" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Excerpt</label>
              <textarea data-testid="cms-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} placeholder="Two-sentence summary that shows on the blog listing and in search results." className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Content</label>
              <MarkdownEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
            </div>
          </div>

          <aside className="space-y-4">
            <div className="card-crisp p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Slug</label>
                <input data-testid="cms-slug" value={form.slug} onChange={(e) => { setSlugDirty(true); setForm({ ...form, slug: toSlug(e.target.value) }); }} placeholder="post-url" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-xs font-mono" />
                {form.slug && <div className="mt-1 text-[11px] text-[#8A94A6]">/blog/{form.slug}</div>}
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Status</label>
                <select data-testid="cms-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm bg-white">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Category</label>
                <input data-testid="cms-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="General" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Author</label>
                <input data-testid="cms-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Tags <span className="text-[#8A94A6]">(comma-separated)</span></label>
                <input data-testid="cms-tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="seo, tracking" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Cover image URL <span className="text-[#8A94A6]">(optional)</span></label>
                <input data-testid="cms-cover" value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://…" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
            </div>

            <div className="card-crisp p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Search preview</div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Meta title override</label>
                <input data-testid="cms-meta-title" value={form.meta_title || ""} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} placeholder={`${form.title} — SerpNex`} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Meta description override</label>
                <textarea data-testid="cms-meta-desc" value={form.meta_description || ""} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={3} placeholder="(uses excerpt)" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Canonical URL <span className="text-[#8A94A6]">(optional)</span></label>
                <input data-testid="cms-canonical" value={form.canonical_url || ""} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} placeholder="/blog/your-slug" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-10">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Content</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Blog posts.</h1>
          <p className="mt-2 text-sm text-[#525B6A] max-w-xl">Write and publish agency-facing SEO content. Drafts stay hidden until you flip the switch to Published.</p>
        </div>
        <button onClick={startNew} data-testid="cms-new" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New post</button>
      </header>

      <div className="mt-8 card-crisp overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-[#F8F9FA] border-b border-[#E2E8F0] text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Updated</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {posts.length === 0 && (
          <div className="p-10 text-center text-[#8A94A6]">
            <FileText className="h-8 w-8 mx-auto mb-3 text-[#E2E8F0]" />
            No posts yet. Click &quot;New post&quot; to write your first one.
          </div>
        )}
        {posts.map((p) => (
          <div key={p.slug} data-testid={`cms-row-${p.slug}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#E2E8F0] text-sm">
            <div className="col-span-5">
              <div className="text-[#0A0D14] font-medium">{p.title}</div>
              <div className="text-xs text-[#8A94A6] font-mono">/blog/{p.slug}</div>
            </div>
            <div className="col-span-2 text-[#525B6A]">{p.category}</div>
            <div className="col-span-2">
              <span className={`pill ${p.status === 'published' ? 'pill-success' : ''}`}>{p.status}</span>
            </div>
            <div className="col-span-2 text-xs text-[#8A94A6]">{new Date(p.updated_at).toLocaleDateString()}</div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              {p.status === "published" && (
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`cms-view-${p.slug}`} title="View" className="p-1.5 text-[#525B6A] hover:text-[#0047FF]"><ExternalLink className="h-3.5 w-3.5" /></a>
              )}
              <button onClick={() => startEdit(p.slug)} data-testid={`cms-edit-${p.slug}`} title="Edit" className="p-1.5 text-[#525B6A] hover:text-[#0047FF]"><Edit3 className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(p.slug)} data-testid={`cms-del-${p.slug}`} title="Delete" className="p-1.5 text-[#525B6A] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
